import React from 'react';
import { type UserVideo } from '@/api/videoApi';
import { UserImage } from '@/api/imageApi';
import { downloadImageFromUrl } from '../common/imageUtils';
import { downloadVideoFromUrl } from '../common/videoUtils';
import { renderPromptInfo, renderInfoField, renderContentSpecificInfo as renderCommonContentSpecificInfo } from '../common/modal/contentRenderUtils';
// Re-export common utilities for backward compatibility
export { renderContentPreview, renderPromptInfo } from '../common/modal/contentRenderUtils';

/**
 * Type guard to check if item is a UserVideo
 */
const isUserVideo = (item: UserVideo | UserImage): item is UserVideo => {
  return 'video_type' in item;
};

/**
 * Type guard to check if item is a UserImage
 */
const isUserImage = (item: UserVideo | UserImage): item is UserImage => {
  return 'image_type' in item;
};

/**
 * Renders content-specific information (video type or image dimensions)
 */
export const renderContentSpecificInfo = (
  isVideo: boolean,
  video: UserVideo | null | undefined,
  isImage: boolean,
  image: UserImage | null | undefined
): React.ReactElement | null => {
  return renderCommonContentSpecificInfo(isVideo, video, isImage, image, 'openAdminVideoModal');
};

/**
 * Renders the content information grid (created, generated by, model, size) - Admin version
 * Uses 'N/A' as default model instead of 'Veo 3 Fast'
 */
export const renderContentInfo = (
  currentItem: UserVideo | UserImage | null | undefined,
  formatDate: (date: string) => string,
  formatFileSize: (size: number) => string,
  isMobile?: boolean
): React.ReactElement | null => {
  if (!currentItem) return null;
  
  const isVideo = isUserVideo(currentItem);
  const isImage = isUserImage(currentItem);
  const video = isVideo ? currentItem : undefined;
  const image = isImage ? currentItem : undefined;
  const shouldTruncate = isMobile ? Boolean(currentItem.user_email) : Boolean(currentItem.user_email && currentItem.user_email.length > 25);
  
  return (
    <div className="space-y-3">
      {/* 2-column grid for mobile, single row for desktop */}
      <div className={isMobile ? "grid grid-cols-2 gap-x-12 gap-y-3 text-sm" : "flex items-start gap-12 text-sm flex-wrap justify-between w-full"}>
        {renderInfoField('Created', formatDate(currentItem.created_at))}
        {renderInfoField(
          'Generated By',
          currentItem.user_email,
          shouldTruncate,
          shouldTruncate ? currentItem.user_email : undefined
        )}
        {renderInfoField('Model', currentItem.model || 'N/A')}
        {renderInfoField('Size', formatFileSize(currentItem.file_size))}
      </div>
      
      {/* Additional info row */}
      <div className="flex items-start gap-12 text-sm flex-wrap">
        {renderContentSpecificInfo(isVideo, video, isImage, image)}
      </div>
    </div>
  );
};



/**
 * Renders the AI model and prompt information section
 */
export const renderAIModelInfo = (
  isVideo: boolean,
  video: UserVideo | null | undefined,
  isImage: boolean,
  image: UserImage | null | undefined,
  showCopiedMessage: boolean,
  handleCopyPrompt: () => void
): React.ReactElement | null => {
  const hasPrompt = (isVideo && video?.prompt) || (isImage && image?.prompt);
  
  if (!hasPrompt) return null;
  
  const prompt = video?.prompt || image?.prompt;
  
  return (
    <div className="space-y-3">
      {renderPromptInfo(prompt, showCopiedMessage, handleCopyPrompt)}
    </div>
  );
};

/**
 * Handles downloading last frame for videos
 */
export const handleDownloadVideoFrame = async (
  video: UserVideo | null | undefined,
  onDownloadLastFrame: ((signedUrl: string) => Promise<void>) | undefined
): Promise<void> => {
  if (video && onDownloadLastFrame) {
    await onDownloadLastFrame(video.video_url);
  }
};

/**
 * Handles downloading image
 */
export const handleDownloadImage = async (
  image: UserImage | null | undefined
): Promise<void> => {
  if (image) {
    const filename = image.file_name || `image_${image.id}.jpg`;
    await downloadImageFromUrl(image.image_url, filename);
  }
};

/**
 * Handles downloading video file
 */
export const handleDownloadVideo = async (
  video: UserVideo | null | undefined
): Promise<void> => {
  if (!video) return;
  
  const filename = (video.title || `video_${video.id}`) + '.mp4';
  await downloadVideoFromUrl(video.id, filename);
};

/**
 * Handles admin delete action for video
 */
export const handleVideoDelete = async (
  onDelete: ((videoId: string) => Promise<void>) | undefined,
  video: UserVideo | null | undefined
): Promise<void> => {
  if (onDelete && video) {
    await onDelete(video.id);
  }
};

/**
 * Handles admin delete action for image
 */
export const handleImageDelete = async (
  onImageDelete: ((imageId: string) => Promise<void>) | undefined,
  image: UserImage | null | undefined
): Promise<void> => {
  if (onImageDelete && image) {
    await onImageDelete(image.id);
  }
};
